import { useMemo } from 'react'
import { Tooltip, Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'

const EMPTY = '—'

const nameFor = (types, id) => types?.find(type => type.id === id)?.name || id || ''

const duration = row => {
  if (!row.startTime || !row.endTime) return ''
  const minutes = dayjs(row.endTime).diff(dayjs(row.startTime), 'minute')
  if (minutes < 0) return ''
  return `${Math.floor(minutes / 60)} hr ${minutes % 60} min`
}

const namesFor = (types, ids) =>
  (ids ?? [])
    .map(id => nameFor(types, id))
    .filter(Boolean)
    .join(', ')

export default function useVisitReportData() {
  const { t } = useTranslation()
  const list = useSelector(state => state.nurse.report.visitList)
  const loading = useSelector(state => state.nurse.report.visitLoading)
  const complaintTypes = useSelector(state => state.lookup.nurse.complaintType)
  const treatmentTypes = useSelector(state => state.lookup.nurse.treatmentType)
  const dispositionTypes = useSelector(state => state.lookup.nurse.dispositionType)

  const rows = useMemo(
    () =>
      list?.flatMap(record =>
        (record.person?.visits ?? []).map(visit => ({
          id: visit.id,
          firstName: record.firstName,
          lastName: record.lastName,
          startTime: visit.startTime,
          endTime: visit.endTime,
          note: visit.note,
          complaintTypeIds: visit.complaintTypeIds,
          treatmentTypeIds: visit.treatmentTypeIds,
          dispositionTypeId: visit.dispositionTypeId,
        })),
      ) ?? [],
    [list],
  )

  const columns = useMemo(
    () => [
      {
        field: 'student',
        headerName: t('Student'),
        flex: 1.2,
        valueGetter: (value, row) => `${row.lastName ?? ''}, ${row.firstName ?? ''}`,
      },
      {
        field: 'date',
        headerName: t('Date'),
        flex: 0.8,
        valueGetter: (value, row) => (row.startTime ? dayjs(row.startTime).format('MM/DD/YYYY') : ''),
      },
      {
        field: 'startTime',
        headerName: t('Time In'),
        flex: 0.7,
        valueGetter: (value, row) => (row.startTime ? dayjs(row.startTime).format('hh:mm A') : ''),
      },
      {
        field: 'endTime',
        headerName: t('Time Out'),
        flex: 0.7,
        valueGetter: (value, row) => (row.endTime ? dayjs(row.endTime).format('hh:mm A') : ''),
        exportValue: row => (row.endTime ? dayjs(row.endTime).format('hh:mm A') : t('In Progress')),
        renderCell: ({ value }) => value || t('In Progress'),
      },
      {
        field: 'duration',
        headerName: t('Duration'),
        flex: 0.7,
        sortable: false,
        valueGetter: (value, row) => duration(row),
        renderCell: ({ value }) => value || EMPTY,
      },
      {
        field: 'complaintTypeIds',
        headerName: t('Complaints'),
        flex: 1.2,
        sortable: false,
        valueGetter: (value, row) => namesFor(complaintTypes, row.complaintTypeIds),
        renderCell: ({ value }) => value || EMPTY,
      },
      {
        field: 'treatmentTypeIds',
        headerName: t('Treatments'),
        flex: 1.2,
        sortable: false,
        valueGetter: (value, row) => namesFor(treatmentTypes, row.treatmentTypeIds),
        renderCell: ({ value }) => value || EMPTY,
      },
      {
        field: 'dispositionTypeId',
        headerName: t('Disposition'),
        flex: 1,
        valueGetter: (value, row) => nameFor(dispositionTypes, row.dispositionTypeId),
        renderCell: ({ value }) => value || EMPTY,
      },
      {
        field: 'note',
        headerName: t('Note'),
        flex: 1.4,
        renderCell: ({ value }) =>
          value ? (
            <Tooltip title={value} arrow placement="bottom" slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line' } } }}>
              <Typography
                variant="body2"
                sx={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {value}
              </Typography>
            </Tooltip>
          ) : (
            EMPTY
          ),
      },
    ],
    [t, complaintTypes, treatmentTypes, dispositionTypes],
  )

  return { rows, columns, loading }
}
